import React, { useEffect } from "react";
import "./view_tracking_card.component.scss";
import Modal from "common_components/ui/modal/modal.ui";
import Assets from "imports/assets.import";
import targetViewModel from "models/target_view.model";
import { ProjectTargetViewRow } from "interfaces/target_view.interface";
import { formatLastViewedAt, useSetState } from "utils/functions.utils";

interface IViewTrackingCard {
    isOpen: boolean;
    onClose: () => void;
    projectId: string;
}

const ViewTrackingCard = (props: IViewTrackingCard) => {
    const [state, setState] = useSetState({
        rows: [] as ProjectTargetViewRow[],
        totalViews: 0,
        loading: true,
        error: '',
    });

    const fetchViews = async () => {
        setState({ loading: true, error: '' });
        try {
            const res = await targetViewModel.getProjectViews(props.projectId);
            setState({
                rows: res?.data?.targets ?? [],
                totalViews: res?.data?.total_views ?? 0,
                loading: false,
            });
        } catch (error) {
            console.error('Error fetching target view tracking details:', error);
            setState({
                // Surfaced in the body below -- a silent empty list would read as "no targets".
                error: typeof error === 'string' ? error : 'Unable to load view tracking details.',
                loading: false,
            });
        }
    }

    useEffect(() => {
        if (props.isOpen) fetchViews();
    }, [props.isOpen, props.projectId]);

    return (
        <Modal isOpen={props.isOpen} onClose={props.onClose}>
            <div className="view-tracking-card" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
                <div className="view-tracking-card-container">
                    <div className="view-tracking-card-header">
                        <div className="view-tracking-card-header-title">View Tracking Details</div>
                        <img className="view-tracking-card-header-close" src={Assets.close} alt="close" onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            props.onClose();
                        }} />
                    </div>

                    {!state.loading && !state.error && (
                        <div className="view-tracking-card-total">{state.totalViews} total views</div>
                    )}

                    <div className="view-tracking-card-body">
                        {state.loading && <div className="view-tracking-card-body-loading">Loading...</div>}

                        {!state.loading && state.error && (
                            <div className="view-tracking-card-body-error">{state.error}</div>
                        )}

                        {!state.loading && !state.error && state.rows.length === 0 && (
                            <div className="view-tracking-card-body-empty">No target images in this project.</div>
                        )}

                        {!state.loading && !state.error && state.rows.map((row: ProjectTargetViewRow) => (
                            <div className="view-tracking-card-body-row" key={row.target_id}>
                                <img
                                    className="view-tracking-card-body-row-thumbnail"
                                    src={row.image_url}
                                    alt="target image"
                                />
                                <div className="view-tracking-card-body-row-info">
                                    <div className="view-tracking-card-body-row-info-count">{row.total_views}</div>
                                    <div className="view-tracking-card-body-row-info-label">
                                        {row.total_views === 1 ? 'view' : 'views'}
                                    </div>
                                </div>
                                <div className="view-tracking-card-body-row-last-viewed">
                                    {formatLastViewedAt(row.last_viewed_at)}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default ViewTrackingCard;
